import * as XLSX from 'xlsx';
import { formatPositionsDisplay } from '../services/positionAPIs';
import { formatDate, calculateDuration } from '../components/leaves/helpers'; // Adjust path as needed

export const exportToExcel = (data, fileName = 'export.xlsx', sheetName = 'Sheet1') => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
};

export const exportDayAbsencesToExcel = (selectedDate, dayAbsences) => {
  // Check if there's any data to export
  if (!dayAbsences || dayAbsences.length === 0) {
    alert('No absence data to export');
    return;
  }

  // Format the date for the filename
  const formattedDate = selectedDate.toISOString().split('T')[0];
  
  // Prepare the data for Excel
  const excelData = dayAbsences.map(absence => ({
    //'Employee ID': absence.employee?.employeeId || 'N/A',
    'First Name': absence.employee?.firstName || 'N/A',
    'Last Name': absence.employee?.lastName || 'N/A',
    'Email': absence.employee?.user?.email || 'N/A',
    'Phone': absence.employee?.phone || 'N/A',
    'Department': absence.employee?.department?.name || 'N/A',
    'Position': formatPositionsDisplay(absence.employee),
    'Absence Period': absence.type || 'N/A',
    'Absence Type': absence.ty_pe || 'N/A',
    'Reason': absence.reason || 'N/A',
    'Start Date': absence.date || 'N/A',
    'End Date': absence.endDate || 'N/A',
    //'Start Time': absence.startTime || 'N/A',
    //'End Time': absence.endTime || 'N/A',
    'Hours': absence.hours || 'N/A',
    //'Status': absence.status || 'N/A',
    //'Notes': absence.notes || 'N/A'
  }));

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const worksheet = XLSX.utils.json_to_sheet(excelData);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Absences');
  
  // Generate Excel file buffer
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
  // Create blob and download
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `absences_${formattedDate}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Map absence types for display
const AbsenceTypeLabels = {
  LATENESS: 'Lateness',
  PARTIAL_DAY: 'Partial Day',
  FULL_DAY: 'Full Day',
  MULTIPLE_DAYS: 'Multiple Days',
  SICKNESS: 'Sickness',
  EARLY_DEPARTURE: 'Early Departure',
  EMERGENCY: 'Emergency',
  PERSONAL: 'Personal',
  APPOINTMENT: 'Appointment',
};

/**
 * Exports absence data to an Excel file
 * @param {Array} absences - List of absence records
 * @param {Object} filters - Current filters applied (for metadata)
 * @param {boolean} unconfirmed - Whether viewing unconfirmed absences
 */
export const exportAbsenceToExcel = (absences, filters, unconfirmed = false) => {
  // Derive type from date/endDate/hours since backend no longer sends it
  const deriveAbsenceType = (date, endDate, hours) => {
    if (date && endDate && date !== endDate) return 'MULTIPLE_DAYS';
    const hrs = parseFloat(hours);
    if (hrs >= 8) return 'FULL_DAY';
    if (hrs > 0) return 'PARTIAL_DAY';
    return 'FULL_DAY';
  };

  const formatHoursExport = (hours) => {
    const h = parseFloat(hours);
    if (!h || h <= 0) return '0m';
    if (h < 1) return `${Math.round(h * 60)}m`;
    const whole = Math.floor(h);
    const mins = Math.round((h - whole) * 60);
    return mins > 0 ? `${whole}h ${mins}m` : `${whole}h`;
  };

  // Helper to extract consistent data from absence object
  const getAbsenceData = (absence) => {
    const source = unconfirmed ? absence.absence || {} : absence;
    const employee = source.employee || {};
    const derivedType = deriveAbsenceType(source.date, source.endDate, source.hours);

    return {
      'Employee Name': `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
      // 'Employee ID': employee.employeeId || 'N/A',
      Department: (typeof employee.department === 'object' ? employee.department?.name : employee.department) || employee.departmentName || 'N/A',
      Office: (typeof employee.office === 'object' ? employee.office?.name : employee.office) || employee.officeName || 'N/A',
      'Absence Period': AbsenceTypeLabels[derivedType] || derivedType,
      // 'Absence Type': AbsenceTypeLabels[source.ty_pe] || source.ty_pe || 'N/A',
      'Start Date': formatDate(source.date),
      'End Date':
        source.endDate
          ? formatDate(source.endDate)
          : formatDate(source.date),

      Duration:
        derivedType === 'MULTIPLE_DAYS' && source.endDate
          ? `${calculateDuration(source.date, source.endDate)} days`
          : derivedType === 'FULL_DAY'
          ? '1 day'
          : derivedType === 'PARTIAL_DAY'
          ? formatHoursExport(source.hours)
          : 'Open',

      // 'Recorded By': unconfirmed
      //   ? `${source.employee?.firstName || ''} ${source.employee?.lastName || ''}`.trim()
      //   : source.recordedBy || 'Unknown',
      Reason: source.reason || 'N/A',
      // Status: (unconfirmed ? absence.status : source.status) || 'Pending',
    };
  };

  // Filter and format data
  const data = absences
    .map(getAbsenceData)
    .filter((row) => {
      // Apply basic filter logic if needed client-side
      let include = true;

      if (filters?.department && (!row.Department || row.Department.trim().toLowerCase() !== filters.department.trim().toLowerCase())) {
        include = false;
      }
      if (filters?.office && (!row.Office || row.Office.trim().toLowerCase() !== filters.office.trim().toLowerCase())) {
        include = false;
      }
      if (filters?.type && row['Absence Period'] !== AbsenceTypeLabels[filters.type]) {
        include = false;
      }
      if (filters?.month && !isNaN(filters.month)) {
        const monthMatch = new Date(row['Start Date']).getMonth() + 1;
        if (monthMatch !== parseInt(filters.month)) include = false;
      }
      if (filters?.year && !isNaN(filters.year)) {
        const yearMatch = new Date(row['Start Date']).getFullYear();
        if (yearMatch !== parseInt(filters.year)) include = false;
      }

      return include;
    });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Absences');

  // Generate filename with timestamp and filters
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
  let filename = `Absences_${dateStr}_${timeStr}.xlsx`;

  // Optional: Add filter context to filename
  const parts = [];
  if (filters?.department) parts.push(`Dept-${filters.department}`);
  if (filters?.office) parts.push(`Office-${filters.office}`);
  if (filters?.type) parts.push(AbsenceTypeLabels[filters.type]);
  if (filters?.month) parts.push(`${new Date(2020, filters.month - 1).toLocaleString('default', { month: 'short' })}`);
  if (filters?.year) parts.push(filters.year);

  if (parts.length > 0) {
    filename = `Absences_${parts.join('_')}_${dateStr}.xlsx`;
  }

  // Trigger download
  XLSX.writeFile(workbook, filename);
};